/**
 * Per-scope collection metadata cache — BET-281 Slice 2, TEN-2398/TEN-2415.
 *
 * Wraps chain.listCollections() with a per-workspace, TTL-bounded cache
 * (see lib/scopedCache.ts). In HTTP/SSE mode one process serves many workspaces,
 * so the cache is partitioned by request scope (hashed API key) — never a single
 * module global — and each entry expires after COLLECTION_CACHE_TTL_MS so a
 * mid-session collection add/rename is visible without a process restart. A
 * collection write also invalidates the current scope for immediate visibility
 * (see invalidateCollectionCacheForScope, wired in tools/collections.ts).
 *
 * Helpers derive runtime behavior from DB fields so consumers never hardcode slugs.
 *
 * Chain: BET-281, STD-101 (derive, don't declare), TEN-2398, TEN-2415.
 */

import { kernelQuery, cacheScope } from "../client.js";
import { ScopedCache, COLLECTION_CACHE_TTL_MS } from "./scopedCache.js";

// ── Types ──────────────────────────────────────────────────────────────────

export interface CollectionDoc {
  _id: string;
  slug: string;
  name: string;
  description?: string;
  purpose?: string;
  icon?: string;
  navGroup?: "daily" | "strategic" | "governance" | "reference" | "collections";
  fields: Array<{
    key: string;
    label: string;
    type: string;
    required?: boolean;
    options?: string[];
    relatesTo?: string;
    searchable?: boolean;
    displayHint?: string;
    zone?: string;
    colorMap?: unknown;
    accentSource?: boolean;
    iconMap?: Record<string, string>;
    helpText?: string;
    /** BET-289: Natural language instruction for how to write this field value. */
    writingGuidance?: string;
    /** BET-289: Example strings showing good/bad field values. */
    writingExamples?: string[];
    optionDescriptions?: unknown;
    semanticRole?: string;
    maxLength?: number;
    minLength?: number;
    requiredContext?: string;
  }>;
  stratum: "system" | "domain" | "working";
  defaultCanonicalKey?: string;
  defaultWorkflowStatus?: string;
  governed?: boolean;
  idPrefix?: string;
  validWorkflowStatuses?: string[];
  classificationCheck?: string;
  classificationPriority?: number;
  qualityCriteria?: Array<{
    field: string;
    rule: "required" | "min_length" | "pattern";
    value?: string;
    active?: boolean;
  }>;
  thinkingLayer?: string;
  timelineRole?: string;
  color?: string;
  governanceRole?: string;
  governanceFunction?: string;
  layoutTemplate?: string;
  usageGuidance?: string;
  examples?: Array<{ name: string; description: string }>;
  crossReferences?: Array<{ slug: string; relationship: string; guidance: string }>;
  descriptionFieldKey?: string;
  /** WP-316: agent-facing guidance on what a well-formed capture for this collection looks like. */
  captureExpectation?: string;
}

// ── Cache State (per-scope, TTL-bounded) ─────────────────────────────────────

interface CollectionCacheValue {
  collections: CollectionDoc[];
  bySlug: Map<string, CollectionDoc>;
}

const collectionCache = new ScopedCache<CollectionCacheValue>(COLLECTION_CACHE_TTL_MS);

// ── Core ─────────────────────────────────────────────────────────────────────

/**
 * Get all workspace collections for the current request scope, cached with a
 * short TTL. First call within the window hits Convex; subsequent calls return
 * cached data. Keyed per workspace so one tenant never sees another's structure.
 */
export async function getCollections(): Promise<CollectionDoc[]> {
  const scope = cacheScope();
  const hit = collectionCache.get(scope);
  if (hit) return hit.collections;
  const result = (await kernelQuery<CollectionDoc[]>("chain.listCollections")) ?? [];
  const bySlug = new Map(result.map((c) => [c.slug, c]));
  collectionCache.set(scope, { collections: result, bySlug });
  return result;
}

/**
 * Get collection by slug from the current scope's cache.
 */
export async function getCollectionBySlug(slug: string): Promise<CollectionDoc | undefined> {
  await getCollections();
  return collectionCache.get(cacheScope())?.bySlug.get(slug);
}

/** Clear ALL scopes — test hook + full flush. */
export function resetCollectionCache(): void {
  collectionCache.clear();
}

/** Invalidate only the current request scope — call after a collection write. */
export function invalidateCollectionCacheForScope(): void {
  collectionCache.delete(cacheScope());
}

// ── Derived Queries ────────────────────────────────────────────────────────

/**
 * Check if a collection supports classification (has non-empty classificationCheck).
 * Runtime equivalent of the old CLASSIFIABLE_COLLECTIONS constant.
 */
export async function isClassifiable(slug: string): Promise<boolean> {
  const col = await getCollectionBySlug(slug);
  return !!col?.classificationCheck;
}

/**
 * Get all classifiable collection slugs.
 */
export async function getClassifiableSlugs(): Promise<string[]> {
  const cols = await getCollections();
  return cols
    .filter((c) => !!c.classificationCheck)
    .map((c) => c.slug);
}

/**
 * Check if a collection uses governed draft workflow.
 * Runtime replacement for the GOVERNED_COLLECTIONS set.
 */
export async function isGoverned(slug: string): Promise<boolean> {
  const col = await getCollectionBySlug(slug);
  return !!col?.governed;
}

/**
 * Get collections by navGroup (e.g. 'governance', 'strategic', 'daily').
 */
export async function getByNavGroup(navGroup: string): Promise<CollectionDoc[]> {
  const cols = await getCollections();
  return cols.filter((c) => c.navGroup === navGroup);
}

/**
 * Get collections by thinkingLayer.
 */
export async function getByThinkingLayer(layer: string): Promise<CollectionDoc[]> {
  const cols = await getCollections();
  return cols.filter((c) => c.thinkingLayer === layer);
}

/**
 * Check if a collection is a "hub" collection — strategic collections that
 * get higher link confidence scores. Original HUB_COLLECTIONS was strategy + features.
 * Primary: governanceRole === 'steering' (captures both strategy + features).
 * Fallback: thinkingLayer === 'strategy' for workspaces not yet backfilled.
 */
export async function isHubCollection(slug: string): Promise<boolean> {
  const col = await getCollectionBySlug(slug);
  if (!col) return false;
  return col.governanceRole === "steering" || col.thinkingLayer === "strategy";
}

/**
 * Get the description field key for a collection.
 * Most collections use "description"; some use "rationale", "belief", "canonical", etc.
 * Falls back to "description" if not specified in the collection's field metadata.
 */
export async function getDescriptionFieldKey(slug: string): Promise<string> {
  const col = await getCollectionBySlug(slug);
  if (!col) return "description";

  // Check for fields with specific semantic roles or known description-like keys
  const descriptionLikeKeys = ["description", "rationale", "belief", "canonical"];
  for (const key of descriptionLikeKeys) {
    const field = col.fields.find((f) => f.key === key);
    if (field && (field.displayHint === "hero" || field.displayHint === "textarea" || field.zone === "body")) {
      return key;
    }
  }

  // Fallback: check for first field in body zone with textarea hint
  const bodyTextField = col.fields.find(
    (f) => f.zone === "body" || f.displayHint === "textarea",
  );
  if (bodyTextField) return bodyTextField.key;

  return "description";
}

/**
 * Check if collection's navGroup is 'governance' — used for binding governance
 * collections (principles, standards, business-rules).
 */
export async function isGovernanceNavGroup(slug: string): Promise<boolean> {
  const col = await getCollectionBySlug(slug);
  return col?.navGroup === "governance";
}

/**
 * Check if a collection belongs to the work planning domain
 * (thinkingLayer is delivery-container, delivery-grouping, or delivery-work).
 * Runtime replacement for checking slug === "chains" || slug === "work-packages".
 */
export async function isWorkPlanningCollection(slug: string): Promise<boolean> {
  const col = await getCollectionBySlug(slug);
  const layer = col?.thinkingLayer;
  return layer === "delivery-container" || layer === "delivery-grouping" || layer === "delivery-work";
}

/**
 * Check if a collection has a specific defaultCanonicalKey.
 */
export async function hasCanonicalKey(slug: string, key: string): Promise<boolean> {
  const col = await getCollectionBySlug(slug);
  return col?.defaultCanonicalKey === key;
}
